import React from "react";
import { ArrowRight, Sparkles, Zap } from "lucide-react";

const COPY = {
  green: {
    eyebrow: "Validated. The work now is execution.",
    title: "You have a Greenlight. Now build it in six minutes.",
    body: "Skip the months of brand, copy, and tech setup. Lightbulb hands you a complete branded business, live, in under six minutes.",
    cta: "Build my business for $497",
  },
  yellow: {
    eyebrow: "You are close. Here is the sharper version.",
    title: "Refine the angle below, then have Lightbulb build that exact business.",
    body: "", // Resolved dynamically based on projected score.
    cta: "Build it. Live in 6 minutes.",
  },
  red: {
    eyebrow: "This one is tough. Pivot, do not push it.",
    title: "Use the Lightbulb Idea Engine to get a business that is already validated.",
    body: "Our Idea Engine hands you an idea our research says actually works for solo founders, then builds the whole business around it.",
    cta: "Get a validated idea for $497",
  },
};

function yellowBody(projected) {
  if (typeof projected === "number" && projected >= 70) {
    return "Your idea is close enough that one specific reframe lifts your score into the green. Build the refined version, not the raw one.";
  }
  if (typeof projected === "number" && projected > 0) {
    return `One specific reframe sharpens this idea into a stronger version (projected ${projected}/100). Build the refined version, not the raw one.`;
  }
  return "One specific reframe sharpens this idea into a stronger version. Build the refined version, not the raw one.";
}

export default function CTABlock({ band = "yellow", reframe = "", projected = null, offerUrl = "https://trylightbulb.com/offer" }) {
  const copy = COPY[band] || COPY.yellow;
  const body = band === "yellow" ? yellowBody(projected) : copy.body;
  return (
    <div
      className="gl-card relative overflow-hidden gl-card-active"
      style={{
        background:
          "radial-gradient(120% 80% at 0% 0%, rgba(74, 222, 128,0.10) 0%, rgba(17,24,18,1) 60%)",
      }}
      data-testid="cta-block"
    >
      <div className="absolute inset-0 gl-grid-bg pointer-events-none" />
      <div className="relative">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={14} className="text-[#4ADE80]" />
          <span className="gl-label">{copy.eyebrow}</span>
        </div>
        <h3 className="text-xl sm:text-3xl font-bold text-white leading-tight max-w-2xl">
          {copy.title}
        </h3>
        <p className="text-[#b6c1b8] mt-3 max-w-2xl leading-relaxed text-[15px] sm:text-base">
          {body}
        </p>

        {band !== "green" && reframe ? (
          <div
            id="sharper-version"
            className="mt-6 p-5 rounded-2xl border border-[#4ADE80]/30 bg-[#4ADE80]/5 scroll-mt-24"
          >
            <div className="flex items-center gap-2 mb-2">
              <Zap size={14} className="text-[#4ADE80]" />
              <span className="gl-label">The Sharper Version</span>
              {typeof projected === "number" && projected > 0 ? (
                <span className="ml-auto text-xs px-2 py-1 rounded-full bg-[#4ADE80]/15 text-[#4ADE80] border border-[#4ADE80]/30">
                  Projected: {projected}/100
                </span>
              ) : null}
            </div>
            <div className="text-white leading-relaxed" data-testid="suggested-reframe">
              {reframe}
            </div>
          </div>
        ) : null}

        <div className="mt-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
          <a
            href={offerUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="primary-cta-button"
            className="gl-cta text-base sm:text-lg"
          >
            {copy.cta}
            <ArrowRight size={18} />
          </a>
          <div className="text-xs text-[#b6c1b8] leading-relaxed max-w-xs">
            Founding Price <span className="text-white font-semibold">$497</span> (next 100 founders).{" "}
            Or 4 interest-free payments of <span className="text-white font-semibold">$125</span>.
          </div>
        </div>
      </div>
    </div>
  );
}
